import { baseApi } from 'services/api';
import { API_VERSION } from 'constants/common';
import {
  CommissionRule,
  CreateCommissionRulePayload,
  CreateCommissionRuleResponse,
  DeleteCommissionRuleResponse,
  GetCommissionRulesPayload,
  GetCommissionRulesResponse,
  UpdateCommissionRuleMembersPayload,
  UpdateCommissionRuleMembersResponse,
  UpdateCommissionRulePayload,
  UpdateCommissionRuleResponse,
  UpdateCommissionRuleStatusPayload,
  UpdateCommissionRuleStatusResponse
} from './commissionSettings.model';
import { getUrlSearchParams } from 'utils/apiUtils';

const CommissionSettingsApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    getCommissionRules: builder.query<GetCommissionRulesResponse, GetCommissionRulesPayload>({
      query: ({ teamId, filter }) => {
        const searchParams = filter ? getUrlSearchParams(filter) : '';
        return {
          url: `${API_VERSION.V1}/merchants/teams/${teamId}/commission-rule?${searchParams}`,
          method: 'GET'
        };
      },
      extraOptions: {
        failure: 'Failed to fetch commission rules',
        showToast: true
      }
    }),
    createCommissionRule: builder.mutation<CreateCommissionRuleResponse, CreateCommissionRulePayload>({
      query: ({ teamId, ...payload }) => ({
        url: `${API_VERSION.V1}/merchants/teams/${teamId}/commission-rule`,
        method: 'POST',
        body: payload
      }),
      extraOptions: {
        failure: 'Failed to create commission rule',
        showToast: true
      }
    }),
    updateCommissionRule: builder.mutation<UpdateCommissionRuleResponse, UpdateCommissionRulePayload>({
      query: ({ teamId, commissionRuleId, ...payload }) => ({
        url: `${API_VERSION.V1}/merchants/teams/${teamId}/commission-rule/${commissionRuleId}`,
        method: 'PUT',
        body: payload
      }),
      extraOptions: {
        failure: 'Failed to update commission rule',
        showToast: true
      }
    }),
    getCommissionRuleById: builder.query<CommissionRule, { teamId: string; commissionRuleId: string }>({
      query: ({ teamId, commissionRuleId }) => ({
        url: `${API_VERSION.V1}/merchants/teams/${teamId}/commission-rule/${commissionRuleId}`,
        method: 'GET'
      }),
      extraOptions: {
        failure: 'Failed to fetch commission rule details',
        showToast: true
      }
    }),
    updateCommissionRuleStatus: builder.mutation<UpdateCommissionRuleStatusResponse, UpdateCommissionRuleStatusPayload>(
      {
        query: ({ teamId, commissionRuleId, status }) => ({
          url: `${API_VERSION.V1}/merchants/teams/${teamId}/commission-rule/${commissionRuleId}/status`,
          method: 'PATCH',
          body: {
            status
          }
        }),
        extraOptions: {
          failure: 'Failed to update commission rule status',
          success: 'Commission rule status updated successfully',
          showToast: true
        }
      }
    ),
    deleteCommissionRule: builder.mutation<DeleteCommissionRuleResponse, { teamId: string; commissionRuleId: string }>({
      query: ({ teamId, commissionRuleId }) => ({
        url: `${API_VERSION.V1}/merchants/teams/${teamId}/commission-rule/${commissionRuleId}`,
        method: 'DELETE'
      }),
      extraOptions: {
        failure: 'Failed to delete commission rule',
        success: 'Commission rule deleted successfully',
        showToast: true
      }
    }),
    updateCommissionRuleMembers: builder.mutation<
      UpdateCommissionRuleMembersResponse,
      UpdateCommissionRuleMembersPayload
    >({
      query: ({ teamId, commissionRuleId, merchantIDs }) => {
        return {
          url: `${API_VERSION.V1}/merchants/teams/${teamId}/commission-rule/${commissionRuleId}/members`,
          method: 'PATCH',
          body: {
            merchantIDs
          }
        };
      },
      extraOptions: {
        failure: 'Failed to update commission rule members',
        success: 'Members updated successfully',
        showToast: true
      }
    })
  })
});

export const {
  useLazyGetCommissionRulesQuery,
  useCreateCommissionRuleMutation,
  useUpdateCommissionRuleMutation,
  useLazyGetCommissionRuleByIdQuery,
  useUpdateCommissionRuleStatusMutation,
  useDeleteCommissionRuleMutation,
  useUpdateCommissionRuleMembersMutation
} = CommissionSettingsApi;
